import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { portfolioApi, analyticsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, Eye, Heart, Users, Edit, LogOut,
  MapPin, Clock, Mail, Linkedin, Twitter, Globe,
  Github, Sparkles, X, Check, Share2, Copy
} from 'lucide-react';

const THEME_STYLES = {
  minimal: {
    bg: 'bg-white',
    header: 'bg-gradient-to-br from-orange-50 to-white',
    accent: 'text-[#e8744f]',
    badge: 'bg-orange-50 text-gray-700 border border-orange-100',
    prompt: 'bg-gray-50 border border-gray-100',
    text: 'text-gray-500',
    name: 'text-gray-900',
    section: 'text-[#e8744f]',
  },
  dark: {
    bg: 'bg-gray-900',
    header: 'bg-gradient-to-br from-gray-800 to-gray-900',
    accent: 'text-orange-400',
    badge: 'bg-gray-700 text-gray-200 border border-gray-600',
    prompt: 'bg-gray-800 border border-gray-700',
    text: 'text-gray-400',
    name: 'text-white',
    section: 'text-orange-400',
  },
  creative: {
    bg: 'bg-orange-50',
    header: 'bg-gradient-to-br from-[#e8744f] to-[#d66440]',
    accent: 'text-[#e8744f]',
    badge: 'bg-orange-100 text-orange-800 border border-orange-200',
    prompt: 'bg-white border border-orange-100',
    text: 'text-orange-900/60',
    name: 'text-white',
    section: 'text-[#e8744f]',
  },
};

export default function MyPortfolio() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [portfolioRes, analyticsRes] = await Promise.all([
        portfolioApi.getMy(),
        analyticsApi.getStats(),
      ]);
      setPortfolio(portfolioRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info('Create your portfolio first!');
        navigate('/builder');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCopyLink = () => {
  const link = `${window.location.origin}/p/${portfolio.id}`;
  navigator.clipboard.writeText(link).then(() => {
    setCopied(true);
    toast.success('Portfolio link copied!');
    setTimeout(() => setCopied(false), 2500);
  });
  };

  const handlePrivacyToggle = async () => {
    try {
      await portfolioApi.update({ is_public: !portfolio.is_public });
      setPortfolio({ ...portfolio, is_public: !portfolio.is_public });
      toast.success('Privacy updated');
    } catch { toast.error('Failed to update'); }
  };

  const handleLookingToggle = async () => {
    try {
      await portfolioApi.update({ actively_looking: !portfolio.actively_looking });
      setPortfolio({ ...portfolio, actively_looking: !portfolio.actively_looking });
      toast.success('Status updated');
    } catch { toast.error('Failed to update'); }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-[#f5f1ed]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e8744f] mx-auto mb-4" />
        <p className="text-gray-500">Loading your portfolio...</p>
      </div>
    </div>
  );

  if (!portfolio) return null;

  const ts = THEME_STYLES[portfolio.theme] || THEME_STYLES.minimal;
  const social = portfolio.social_links || {};
  const prompts = [
    { icon: '🌟', q: 'What makes me different', val: portfolio.uniqueness },
    { icon: '🔥', q: 'What excites me', val: portfolio.passion },
    { icon: '🏆', q: "I'm most proud of", val: portfolio.proudest_work },
    { icon: '💭', q: 'Dream project', val: portfolio.dream_project },
    { icon: '☀️', q: 'A typical day', val: portfolio.typical_day },
    { icon: '💬', q: 'Best career advice', val: portfolio.career_advice },
  ].filter(p => p.val);

  const shareLink = `${window.location.origin}/p/${portfolio.id}`;

  return (
    <div className="min-h-screen bg-[#f5f1ed]">
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Home</span>
        </button>
        <span className="font-bold text-[#e8744f] text-sm">✨ My Portfolio</span>
        <button onClick={handleLogout} className="flex items-center gap-1.5 text-red-500 hover:text-red-700 transition-colors text-sm">
          <LogOut size={16} />
          <span>Logout</span>
        </button>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">

        {/* Analytics Row */}
        {analytics && (
          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: Eye, label: 'Views', val: analytics.views, color: 'text-blue-500', bg: 'bg-blue-50' },
              { icon: Heart, label: 'Likes', val: analytics.likes_received, color: 'text-red-500', bg: 'bg-red-50' },
              { icon: Users, label: 'Matches', val: analytics.matches, color: 'text-green-500', bg: 'bg-green-50' },
            ].map(({ icon: Icon, label, val, color, bg }) => (
              <div key={label} className="bg-white rounded-2xl p-4 text-center shadow-sm border border-gray-100">
                <div className={`w-10 h-10 ${bg} rounded-full flex items-center justify-center mx-auto mb-2`}>
                  <Icon size={18} className={color} />
                </div>
                <div className="text-2xl font-bold text-gray-900">{val}</div>
                <div className="text-xs text-gray-500">{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Share Link Card */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <Share2 size={15} className="text-[#e8744f]" />
            <span className="text-sm font-semibold text-gray-800">Share your portfolio</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-500 truncate font-mono">
              {shareLink}
            </div>
            <button
              onClick={handleCopyLink}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                copied
                  ? 'bg-green-100 text-green-600 border border-green-200'
                  : 'bg-[#e8744f] hover:bg-[#d66440] text-white shadow-sm'
              }`}
            >
              {copied ? <><Check size={13} /> Copied!</> : <><Copy size={13} /> Copy</>}
            </button>
          </div>
          {!portfolio.is_public && (
            <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
              ⚠️ Your profile is set to Private — enable Public in Settings below for the link to work.
            </p>
          )}
        </div>

        {/* Portfolio Card */}
        <div className={`rounded-3xl overflow-hidden shadow-lg border border-gray-100 ${ts.bg}`}>

          {/* Header */}
          <div className={`p-6 pb-5 ${ts.header}`}>
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div className={`w-16 h-16 rounded-full overflow-hidden flex-shrink-0 border-2 ${portfolio.theme === 'creative' ? 'border-white/30' : 'border-orange-200'}`}>
                {portfolio.avatar_url ? (
                  <img src={portfolio.avatar_url} alt={portfolio.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-[#e8744f] flex items-center justify-center text-white text-2xl font-bold">
                    {portfolio.name?.charAt(0)}
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h1 className={`text-2xl font-bold truncate ${ts.name}`}>{portfolio.name}</h1>
                <p className={`text-sm ${portfolio.theme === 'creative' ? 'text-white/80' : ts.text}`}>{portfolio.title}</p>

                {/* Badges Row */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {(portfolio.profession || portfolio.branch) && (
                    <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                    🎓 {[portfolio.branch, portfolio.profession].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {portfolio.location && (
                    <span className={`flex items-center gap-1 text-xs ${portfolio.theme === 'creative' ? 'text-white/70' : ts.text}`}>
                      <MapPin size={10} />{portfolio.location}
                    </span>
                  )}
                  {portfolio.years_experience && (
                    <span className={`flex items-center gap-1 text-xs ${portfolio.theme === 'creative' ? 'text-white/70' : ts.text}`}>
                      <Clock size={10} />{portfolio.years_experience}+ yrs
                    </span>
                  )}
                  {portfolio.availability && (
                    <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                      portfolio.availability === 'Actively looking' ? 'bg-green-500/20 text-green-600' :
                      portfolio.availability === 'Not available' ? 'bg-red-500/20 text-red-500' :
                      'bg-orange-500/20 text-orange-600'
                    }`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {portfolio.availability}
                    </span>
                  )}
                  {portfolio.actively_looking && !portfolio.availability && (
                    <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      Actively looking
                    </span>
                  )}
                </div>
              </div>

              <div className={`rounded-full p-2 ${portfolio.theme === 'creative' ? 'bg-white/20' : 'bg-orange-100'}`}>
                <Sparkles size={16} className={portfolio.theme === 'creative' ? 'text-white' : 'text-[#e8744f]'} />
              </div>
            </div>

            {portfolio.tagline && (
              <p className={`mt-4 text-sm italic ${portfolio.theme === 'creative' ? 'text-white/80' : ts.text}`}>
                "{portfolio.tagline}"
              </p>
            )}
          </div>

          {/* Gallery */}
          {portfolio.gallery?.length > 0 && (
            <div className="px-5 py-3 flex gap-2 overflow-x-auto">
              {portfolio.gallery.map((url, i) => (
                <div key={i} className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                  <img src={url} alt="gallery" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          {/* Content */}
          <div className="p-5 space-y-5">

            {/* Bio */}
            {portfolio.bio && (
              <div>
                <h3 className={`font-bold text-base mb-2 ${ts.section}`}>About Me</h3>
                <p className={`text-sm leading-relaxed ${ts.text}`}>{portfolio.bio}</p>
              </div>
            )}

            {/* Skills */}
            {portfolio.skills?.length > 0 && (
              <div>
                <h3 className={`font-bold text-base mb-2 ${ts.section}`}>Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {portfolio.skills.map((s, i) => (
                    <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium ${ts.badge}`}>{s}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Tools */}
            {portfolio.tools?.length > 0 && (
              <div>
                <h3 className={`font-bold text-base mb-2 ${ts.section}`}>Tools</h3>
                <div className="flex flex-wrap gap-2">
                  {portfolio.tools.map((t, i) => (
                    <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium ${ts.badge}`}>{t}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Languages */}
            {portfolio.languages?.length > 0 && (
              <div>
                <h3 className={`font-bold text-base mb-2 ${ts.section}`}>Languages</h3>
                <div className="flex flex-wrap gap-2">
                  {portfolio.languages.map((l, i) => (
                    <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium ${ts.badge}`}>{l}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Projects */}
            {portfolio.projects?.length > 0 && (
              <div>
                <h3 className={`font-bold text-base mb-3 ${ts.section}`}>Projects</h3>
                <div className="grid grid-cols-2 gap-3">
                  {portfolio.projects.map((p, i) => (
                    <div key={i} className={`rounded-2xl overflow-hidden ${ts.prompt}`}>
                      {p.image_url && (
                        <img src={p.image_url} alt={p.title} className="w-full h-28 object-cover" />
                      )}
                      <div className="p-3">
                        <p className={`text-sm font-bold ${portfolio.theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>{p.title}</p>
                        {p.description && <p className={`text-xs mt-0.5 line-clamp-2 ${ts.text}`}>{p.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Personality Prompts */}
            {prompts.length > 0 && (
              <div>
                <h3 className={`font-bold text-base mb-3 ${ts.section}`}>Get to Know Me</h3>
                <div className="space-y-3">
                  {prompts.map((p, i) => (
                    <div key={i} className={`rounded-2xl p-4 ${ts.prompt}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-base">{p.icon}</span>
                        <span className={`text-xs font-bold ${ts.accent}`}>{p.q}</span>
                      </div>
                      <p className={`text-sm leading-relaxed ${ts.text}`}>{p.val}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Social Links */}
            {(social.linkedin || social.github || social.twitter || social.portfolio_url) && (
              <div>
                <h3 className={`font-bold text-base mb-2 ${ts.section}`}>Connect</h3>
                <div className="flex flex-wrap gap-2">
                  {social.linkedin && (
                    <a href={social.linkedin.startsWith('http') ? social.linkedin : `https://${social.linkedin}`}
                      target="_blank" rel="noreferrer"
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium transition hover:scale-105 ${ts.badge}`}>
                      <Linkedin size={12} /> LinkedIn
                    </a>
                  )}
                  {social.github && (
                    <a href={social.github.startsWith('http') ? social.github : `https://${social.github}`}
                      target="_blank" rel="noreferrer"
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium transition hover:scale-105 ${ts.badge}`}>
                      <Github size={12} /> GitHub
                    </a>
                  )}
                  {social.twitter && (
                    <a href={social.twitter.startsWith('@') ? `https://twitter.com/${social.twitter.slice(1)}` : `https://twitter.com/${social.twitter}`}
                      target="_blank" rel="noreferrer"
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium transition hover:scale-105 ${ts.badge}`}>
                      <Twitter size={12} /> Twitter
                    </a>
                  )}
                  {social.portfolio_url && (
                    <a href={social.portfolio_url.startsWith('http') ? social.portfolio_url : `https://${social.portfolio_url}`}
                      target="_blank" rel="noreferrer"
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium transition hover:scale-105 ${ts.badge}`}>
                      <Globe size={12} /> Website
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Contact */}
            {portfolio.contact_email && (
              <div className={`flex items-center gap-2 rounded-xl p-3 ${ts.prompt}`}>
                <Mail size={14} className={ts.accent} />
                <span className={`text-sm ${ts.text}`}>{portfolio.contact_email}</span>
              </div>
            )}
          </div>
        </div>

        {/* Settings Card */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <h2 className="font-bold text-base mb-4">⚙️ Settings</h2>
          <div className="space-y-3">
            {[
              { label: 'Public Profile', sub: portfolio.is_public ? 'Visible to everyone' : 'Private', field: 'is_public', handler: handlePrivacyToggle },
              { label: 'Actively Looking', sub: portfolio.actively_looking ? 'Showing green badge' : 'Not showing', field: 'actively_looking', handler: handleLookingToggle },
            ].map(({ label, sub, field, handler }) => (
              <div key={field} className="flex items-center justify-between p-3 rounded-xl bg-gray-50">
                <div>
                  <p className="text-sm font-medium">{label}</p>
                  <p className="text-xs text-gray-400">{sub}</p>
                </div>
                <div onClick={handler} className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative ${portfolio[field] ? 'bg-[#e8744f]' : 'bg-gray-200'}`}>
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${portfolio[field] ? 'translate-x-5' : ''}`} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Edit Button */}
        <button
          onClick={() => navigate('/edit-portfolio')}
          className="w-full bg-[#e8744f] hover:bg-[#d66440] text-white font-semibold py-4 rounded-2xl flex items-center justify-center gap-2 transition-colors shadow-lg"
        >
          <Edit size={18} />
          Edit Portfolio
        </button>

      </div>
    </div>
  );
}